<template>
  <div>
    <!-- Authentication Check -->
    <div v-if="!authenticated">
      <LoginForm @login-success="handleLoginSuccess" />
    </div>
    
    <!-- Admin Panel Content -->
    <div v-else>
    <h4>Admin panel</h4>
    
    <div class="row">
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5>Szakirányok kezelése</h5>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <div class="input-group">
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="newMajor"
                  placeholder="Új szakirány neve"
                  @keyup.enter="addMajor"
                >
                <button 
                  type="button" 
                  class="btn btn-primary"
                  @click="addMajor"
                  :disabled="!newMajor.trim()"
                >
                  Hozzáadás
                </button>
              </div>
            </div>
            
            <div class="list-group" style="max-height: 300px; overflow-y: auto;">
              <div 
                v-for="major in majors" 
                :key="major.id"
                class="list-group-item d-flex justify-content-between align-items-center"
              >
                <div class="flex-grow-1">
                  <span v-if="editingMajor !== major.id">{{ major.name }}</span>
                  <input 
                    v-else
                    type="text"
                    class="form-control form-control-sm"
                    v-model="editMajorName"
                    @keyup.enter="saveMajor(major.id)"
                    @keyup.esc="cancelEditMajor"
                    ref="majorEditInput"
                  >
                </div>
                <div class="d-flex align-items-center">
                  <small class="text-muted me-2">{{ formatDate(major.created_at) }}</small>
                  <div class="btn-group" role="group">
                    <button 
                      v-if="editingMajor !== major.id"
                      type="button" 
                      class="btn btn-sm btn-outline-primary"
                      @click="startEditMajor(major)"
                      title="Szerkesztés"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button 
                      v-else
                      type="button" 
                      class="btn btn-sm btn-success"
                      @click="saveMajor(major.id)"
                      title="Mentés"
                    >
                      <i class="fas fa-check"></i>
                    </button>
                    <button 
                      v-if="editingMajor !== major.id"
                      type="button" 
                      class="btn btn-sm btn-outline-danger"
                      @click="deleteMajor(major.id)"
                      title="Törlés"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                    <button 
                      v-else
                      type="button" 
                      class="btn btn-sm btn-secondary"
                      @click="cancelEditMajor"
                      title="Mégse"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">
            <h5>Nyelvek kezelése</h5>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <div class="input-group">
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="newLanguage"
                  placeholder="Új nyelv neve"
                  @keyup.enter="addLanguage"
                >
                <button 
                  type="button" 
                  class="btn btn-primary"
                  @click="addLanguage"
                  :disabled="!newLanguage.trim()"
                >
                  Hozzáadás
                </button>
              </div>
            </div>
            
            <div class="list-group" style="max-height: 300px; overflow-y: auto;">
              <div 
                v-for="language in languages" 
                :key="language.id"
                class="list-group-item d-flex justify-content-between align-items-center"
              >
                <div class="flex-grow-1">
                  <span v-if="editingLanguage !== language.id">{{ language.name }}</span>
                  <input 
                    v-else
                    type="text"
                    class="form-control form-control-sm"
                    v-model="editLanguageName"
                    @keyup.enter="saveLanguage(language.id)"
                    @keyup.esc="cancelEditLanguage"
                    ref="languageEditInput"
                  >
                </div>
                <div class="d-flex align-items-center">
                  <small class="text-muted me-2">{{ formatDate(language.created_at) }}</small>
                  <div class="btn-group" role="group">
                    <button 
                      v-if="editingLanguage !== language.id"
                      type="button" 
                      class="btn btn-sm btn-outline-primary"
                      @click="startEditLanguage(language)"
                      title="Szerkesztés"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button 
                      v-else
                      type="button" 
                      class="btn btn-sm btn-success"
                      @click="saveLanguage(language.id)"
                      title="Mentés"
                    >
                      <i class="fas fa-check"></i>
                    </button>
                    <button 
                      v-if="editingLanguage !== language.id"
                      type="button" 
                      class="btn btn-sm btn-outline-danger"
                      @click="deleteLanguage(language.id)"
                      title="Törlés"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                    <button 
                      v-else
                      type="button" 
                      class="btn btn-sm btn-secondary"
                      @click="cancelEditLanguage"
                      title="Mégse"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Student Management Section -->
    <div class="card mt-4">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-center">
          <h5>Hallgatók kezelése</h5>
          <div class="d-flex align-items-center">
            <label for="majorFilter" class="form-label me-2 mb-0">Szűrés szakirány szerint:</label>
            <select id="majorFilter" class="form-select" style="width: auto;" v-model="selectedMajorFilter" @change="filterStudents">
              <option value="">Minden szakirány</option>
              <option v-for="major in majors" :key="major.id" :value="major.name">
                {{ major.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <strong>{{ filteredStudents.length }}</strong> hallgató 
            <span v-if="selectedMajorFilter" class="text-muted">- {{ selectedMajorFilter }}</span>
          </div>
          <button 
            type="button" 
            class="btn btn-success"
            @click="exportFilteredToExcel"
            :disabled="filteredStudents.length === 0 || exporting"
          >
            <span v-if="exporting" class="spinner-border spinner-border-sm me-2"></span>
            {{ exporting ? 'Exportálás...' : 'Excel export' }}
          </button>
        </div>
        
        <div v-if="filteredStudents.length === 0" class="text-center text-muted py-3">
          Nincsenek hallgatók a kiválasztott szakirányban.
        </div>
        
        <div v-else class="table-responsive" style="max-height: 400px; overflow-y: auto;">
          <table class="table table-sm table-striped">
            <thead class="sticky-top bg-light">
              <tr>
                <th>Név</th>
                <th>E-mail</th>
                <th>Szakirány</th>
                <th>Tanulmányok</th>
                <th>Létrehozva</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="student in filteredStudents" :key="student.id">
                <td class="fw-bold">{{ student.full_name }}</td>
                <td>
                  <a v-if="student.email" :href="`mailto:${student.email}`" class="text-decoration-none">
                    {{ student.email }}
                  </a>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>{{ student.major || '-' }}</td>
                <td>
                  <span v-if="student.education_level" class="badge bg-info">
                    {{ student.education_level }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-muted small">
                  {{ formatDate(student.created_at) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="card mt-4">
      <div class="card-header">
        <h5>Statisztikák</h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-3">
            <div class="card bg-primary text-white">
              <div class="card-body text-center">
                <h3>{{ stats.totalStudents }}</h3>
                <p class="mb-0">Összes hallgató</p>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card bg-success text-white">
              <div class="card-body text-center">
                <h3>{{ stats.totalMajors }}</h3>
                <p class="mb-0">Szakirányok</p>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card bg-info text-white">
              <div class="card-body text-center">
                <h3>{{ stats.totalLanguages }}</h3>
                <p class="mb-0">Nyelvek</p>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="card bg-warning text-white">
              <div class="card-body text-center">
                <h3>{{ stats.universitystudents }}</h3>
                <p class="mb-0">Egyetemista</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Logout Button -->
    <div class="d-flex justify-content-end mb-3">
      <button class="btn btn-outline-danger" @click="logout">
        Kijelentkezés
      </button>
    </div>
    </div>

    <!-- Success message -->
    <div v-if="showSuccess" class="alert alert-success alert-dismissible fade show position-fixed" style="top: 20px; right: 20px; z-index: 1050">
      {{ successMessage }}
      <button type="button" class="btn-close" @click="showSuccess = false"></button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '../config/api.js'
import LoginForm from './LoginForm.vue'

export default {
  name: 'AdminPanel',
  components: {
    LoginForm
  },
  data() {
    return {
      authenticated: false,
      user: null,
      majors: [],
      languages: [],
      newMajor: '',
      newLanguage: '',
      stats: {
        totalStudents: 0,
        totalMajors: 0,
        totalLanguages: 0,
        universitystudents: 0
      },
      showSuccess: false,
      successMessage: '',
      editingMajor: null,
      editMajorName: '',
      editingLanguage: null,
      editLanguageName: '',
      selectedMajorFilter: '',
      filteredStudents: [],
      exporting: false
    }
  },
  async mounted() {
    await this.checkAuth()
    if (this.authenticated) {
      await this.loadData()
      this.filterStudents()
    }
  },
  methods: {
    async checkAuth() {
      try {
        const response = await axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.AUTH.STATUS, {
          withCredentials: true
        })
        this.authenticated = response.data.authenticated
        this.user = response.data.user
      } catch (error) {
        console.error('Auth check error:', error)
        this.authenticated = false
      }
    },
    handleLoginSuccess(user) {
      this.authenticated = true
      this.user = user
      this.loadData()
      this.$emit('login-success', user)
    },
    async logout() {
      try {
        await axios.post(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.AUTH.LOGOUT, {}, {
          withCredentials: true
        })
        this.authenticated = false
        this.user = null
        this.$emit('logout')
      } catch (error) {
        console.error('Logout error:', error)
        this.$emit('logout')
      }
    },
    async loadData() {
      await Promise.all([
        this.loadMajors(),
        this.loadLanguages(),
        this.loadStats()
      ])
    },
    async loadMajors() {
      try {
        const response = await axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.MAJORS)
        this.majors = response.data
        this.stats.totalMajors = this.majors.length
      } catch (error) {
        console.error('Error loading majors:', error)
      }
    },
    async loadLanguages() {
      try {
        const response = await axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.LANGUAGES)
        this.languages = response.data
        this.stats.totalLanguages = this.languages.length
      } catch (error) {
        console.error('Error loading languages:', error)
      }
    },
    async loadStats() {
      try {
        const response = await axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.STUDENTS)
        const students = response.data
        this.filteredStudents = students
        this.stats.totalStudents = students.length
        this.stats.universitystudents = students.filter(s => s.education_level === 'Egyetemista').length
      } catch (error) {
        console.error('Error loading stats:', error)
      }
    },
    async addMajor() {
      if (!this.newMajor.trim()) return
      
      try {
        const response = await axios.post(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.MAJORS, { 
          name: this.newMajor.trim() 
        }, {
          withCredentials: true
        })
        this.majors.push(response.data)
        this.newMajor = ''
        this.stats.totalMajors = this.majors.length
        this.showSuccessMessage('Szakirány sikeresen hozzáadva!')
      } catch (error) {
        console.error('Error adding major:', error)
      }
    },
    async addLanguage() {
      if (!this.newLanguage.trim()) return
      
      try {
        const response = await axios.post(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.LANGUAGES, { 
          name: this.newLanguage.trim() 
        }, {
          withCredentials: true
        })
        this.languages.push(response.data)
        this.newLanguage = ''
        this.stats.totalLanguages = this.languages.length
        this.showSuccessMessage('Nyelv sikeresen hozzáadva!')
      } catch (error) {
        console.error('Error adding language:', error)
      }
    },
    showSuccessMessage(message) {
      this.successMessage = message
      this.showSuccess = true
      setTimeout(() => { this.showSuccess = false }, 3000)
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('hu-HU')
    },
    startEditMajor(major) {
      this.editingMajor = major.id
      this.editMajorName = major.name
      this.$nextTick(() => {
        if (this.$refs.majorEditInput && this.$refs.majorEditInput[0]) {
          this.$refs.majorEditInput[0].focus()
        }
      })
    },
    cancelEditMajor() {
      this.editingMajor = null
      this.editMajorName = ''
    },
    async saveMajor(id) {
      if (!this.editMajorName.trim()) return
      
      try {
        const response = await axios.put(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MAJORS}/${id}`, { 
          name: this.editMajorName.trim() 
        }, {
          withCredentials: true
        })
        
        const majorIndex = this.majors.findIndex(m => m.id === id)
        if (majorIndex !== -1) {
          this.majors[majorIndex].name = response.data.name
        }
        
        this.editingMajor = null
        this.editMajorName = ''
        this.showSuccessMessage('Szakirány sikeresen frissítve!')
      } catch (error) {
        console.error('Error updating major:', error)
        alert('Hiba történt a szakirány frissítése során!')
      }
    },
    async deleteMajor(id) {
      if (!confirm('Biztosan törölni szeretné ezt a szakirányt?')) return
      
      try {
        await axios.delete(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MAJORS}/${id}`, {
          withCredentials: true
        })
        
        this.majors = this.majors.filter(m => m.id !== id)
        this.stats.totalMajors = this.majors.length
        this.showSuccessMessage('Szakirány sikeresen törölve!')
      } catch (error) {
        console.error('Error deleting major:', error)
        alert('Hiba történt a szakirány törlése során!')
      }
    },
    startEditLanguage(language) {
      this.editingLanguage = language.id
      this.editLanguageName = language.name
      this.$nextTick(() => {
        if (this.$refs.languageEditInput && this.$refs.languageEditInput[0]) {
          this.$refs.languageEditInput[0].focus()
        }
      })
    },
    cancelEditLanguage() {
      this.editingLanguage = null
      this.editLanguageName = ''
    },
    async saveLanguage(id) {
      if (!this.editLanguageName.trim()) return
      
      try {
        const response = await axios.put(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LANGUAGES}/${id}`, { 
          name: this.editLanguageName.trim() 
        }, {
          withCredentials: true
        })
        
        const languageIndex = this.languages.findIndex(l => l.id === id)
        if (languageIndex !== -1) {
          this.languages[languageIndex].name = response.data.name
        }
        
        this.editingLanguage = null
        this.editLanguageName = ''
        this.showSuccessMessage('Nyelv sikeresen frissítve!')
      } catch (error) {
        console.error('Error updating language:', error)
        alert('Hiba történt a nyelv frissítése során!')
      }
    },
    async deleteLanguage(id) {
      if (!confirm('Biztosan törölni szeretné ezt a nyelvet?')) return
      
      try {
        await axios.delete(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LANGUAGES}/${id}`, {
          withCredentials: true
        })
        
        this.languages = this.languages.filter(l => l.id !== id)
        this.stats.totalLanguages = this.languages.length
        this.showSuccessMessage('Nyelv sikeresen törölve!')
      } catch (error) {
        console.error('Error deleting language:', error)
        alert('Hiba történt a nyelv törlése során!')
      }
    },
    filterStudents() {
      if (!this.selectedMajorFilter) {
        // Show all students
        this.loadStats() // This will reload all students into filteredStudents
      } else {
        // Filter by selected major
        axios.get(API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.STUDENTS)
          .then(response => {
            this.filteredStudents = response.data.filter(student => 
              student.major === this.selectedMajorFilter
            )
          })
          .catch(error => {
            console.error('Error filtering students:', error)
          })
      }
    },
    async exportFilteredToExcel() {
      if (this.filteredStudents.length === 0) return
      
      this.exporting = true
      try {
        // Create query parameter for major filter
        const queryParam = this.selectedMajorFilter 
          ? `?major=${encodeURIComponent(this.selectedMajorFilter)}`
          : ''
        
        const response = await axios.get(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXPORT.EXCEL}${queryParam}`, {
          responseType: 'blob'
        })
        
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        })
        
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        
        const now = new Date()
        const timestamp = now.toISOString().slice(0, 10)
        const majorSuffix = this.selectedMajorFilter 
          ? `-${this.selectedMajorFilter.replace(/[^a-zA-Z0-9]/g, '-')}`
          : ''
        link.download = `kaszk-hallgatok${majorSuffix}-${timestamp}.xlsx`
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.showSuccessMessage('Excel export sikeres!')
      } catch (error) {
        console.error('Export error:', error)
        alert('Hiba történt az export során!')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>